'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { PointFeature } from 'supercluster';
import { useMap } from '@vis.gl/react-google-maps';
import useSupercluster from 'use-supercluster';
import { MAX_ZOOM } from 'src/components/constants';
import { useLogging } from 'src/context/logging';
import { FeatureMarker, FeaturesClusterMarker } from '../ProviderMarkers';

/** A provider (or site) the map API returned, with its coordinates. */
export interface ProviderMapPoint {
  id?: string | number;
  provider_id?: string | number;
  lat: number;
  lng: number;
  /** How many providers this point stands for (an aggregated cell); one when absent. */
  providerCount?: number;
  [key: string]: unknown;
}

interface ProviderFeatureProperties {
  provider_id?: string | number;
  details: ProviderMapPoint;
  providerCount: number;
}

interface ClusteredProviderMarkersProps {
  /** Undefined while there are no results, which also clears the marker cache. */
  points: ProviderMapPoint[] | undefined;
  /** Visible area as `[west, south, east, north]`. */
  bounds: [number, number, number, number] | undefined;
  zoom: number;
  activeMarker: string | null;
  setActiveMarker: (markerId: string | null) => void;
  addToList?: (details: unknown) => void;
}

/** Cluster radius in pixels for a zoom; zero at max zoom, so nothing clusters. */
function clusterRadiusForZoom(zoom: number): number {
  if (zoom >= MAX_ZOOM) return 0;
  if (zoom > 14) return 25;
  if (zoom < 11 && zoom > 8) return 50;
  return 75;
}

/** Stable identity for a feature, so a marker keeps its element across renders. */
function getStableKey(feature: {
  id?: string | number;
  properties: { provider_id?: string | number; id?: string | number } | null;
  geometry: { coordinates: number[] };
}): string {
  const id = feature.id ?? feature.properties?.provider_id ?? feature.properties?.id;
  const [lng, lat] = feature.geometry.coordinates;
  return `${id}-${lng}-${lat}`;
}

/**
 * Provider markers on a Google Map, clustered with supercluster. Shared by
 * Discovery and Scout; render it inside a `<Map>`.
 *
 * Features keep one object per stable key between renders, so markers don't
 * flicker when results refresh.
 */
export default function ClusteredProviderMarkers({
  points,
  bounds,
  zoom,
  activeMarker,
  setActiveMarker,
  addToList,
}: ClusteredProviderMarkersProps) {
  const map = useMap();
  const log = useLogging();
  // Last render's features by stable key: a non-React cache that must not
  // trigger renders.
  const previousFeaturesRef = useRef(new Map());

  const stabilizeFeatures = useCallback(<F extends Parameters<typeof getStableKey>[0]>(
    newFeatures: F[]
  ): F[] => {
    const nextFeatures = new Map();
    const result = newFeatures.map((feature) => {
      const key = getStableKey(feature);
      const existing = previousFeaturesRef.current.get(key);
      if (existing) {
        Object.assign(existing.properties, feature.properties);
        Object.assign(existing.geometry, feature.geometry);
        nextFeatures.set(key, existing);
        return existing as F;
      }
      nextFeatures.set(key, feature);
      return feature;
    });
    previousFeaturesRef.current = nextFeatures;
    return result;
  }, []);

  // Clear the cache when results are emptied (e.g. "Search without Codes").
  useEffect(() => {
    if (!points) {
      previousFeaturesRef.current = new Map();
    }
  }, [points]);

  // Raw points as GeoJSON features for supercluster. The full point is kept
  // in properties.details because supercluster only preserves properties.
  const features = useMemo(() => {
    if (!points?.length) return [];
    const mapped: PointFeature<ProviderFeatureProperties>[] = points.map((point) => ({
      type: 'Feature',
      properties: {
        provider_id: point.provider_id ?? point.id,
        details: point,
        providerCount: point.providerCount ?? 1,
      },
      geometry: {
        type: 'Point',
        coordinates: [point.lng, point.lat],
      },
    }));
    return stabilizeFeatures(mapped);
  }, [points, stabilizeFeatures]);

  const radius = useMemo(() => clusterRadiusForZoom(zoom), [zoom]);

  // map/reduce only aggregate cluster properties; individual points come back
  // as they went in. A cluster shows how many providers it holds, never their volume.
  const clusterOptions = useMemo(
    () => ({
      radius,
      maxZoom: MAX_ZOOM,
      map: (props: ProviderFeatureProperties) => ({
        count: props.providerCount ?? 1,
      }),
      reduce: (acc: { count: number }, props: { count: number }) => {
        acc.count += props.count;
      },
    }),
    [radius]
  );

  const { clusters: rawClusters, supercluster } = useSupercluster({
    points: features,
    bounds,
    zoom,
    options: clusterOptions,
  });

  const clusters = useMemo(
    () => stabilizeFeatures(rawClusters ?? []),
    [rawClusters, stabilizeFeatures]
  );

  const handleClusterClick = (clusterId: string, lat: number, lng: number) => {
    const marker = clusterId === activeMarker ? null : clusterId;
    if (zoom >= MAX_ZOOM - 1) {
      setActiveMarker(marker);
    } else {
      map?.panTo({ lat, lng });
      map?.setZoom(Math.min(zoom + 2, MAX_ZOOM));
      setActiveMarker(null);
    }
  };

  // A click selects the provider rather than toggling it: a hover already made it
  // active, and a click that cleared it would drop the pin and its results row's
  // highlight while the popup is still open.
  const handleMarkerClick = (markerId: string) => {
    setActiveMarker(markerId);
  };

  return (
    <>
      {clusters.map((feature) => {
        const [lng, lat] = feature.geometry.coordinates;
        const properties = feature.properties as Record<string, unknown> | null;
        const isCluster = properties?.cluster === true;
        const stableKey = getStableKey(feature);

        // A cluster shows how many providers it holds; a point shows how many it
        // stands for (one provider, or an aggregated cell).
        const size = isCluster
          ? Number(properties?.count ?? properties?.point_count)
          : Number(properties?.providerCount ?? 1);
        const markerId = isCluster
          ? feature.id?.toString()
          : String(properties?.provider_id ?? '');
        const isHovered = activeMarker === markerId;

        if (isCluster) {
          let leaves: PointFeature<ProviderFeatureProperties>[] = [];
          try {
            leaves = supercluster?.getLeaves(Number(feature.id), 200) ?? [];
          } catch (error) {
            log.exception('Error getting leaves for cluster id', error);
          }
          const showLeaves =
            leaves.length > 1 &&
            leaves.some((leaf) => leaf?.properties?.details?.name);

          return (
            <FeaturesClusterMarker
              key={stableKey}
              clusterId={markerId}
              position={{ lat, lng }}
              size={size}
              sizeAsText={String(size)}
              isHovered={isHovered}
              addToList={addToList}
              clusterLeaves={showLeaves ? leaves : []}
              onMarkerClick={() => handleClusterClick(markerId, lat, lng)}
              onMouseOver={() => setActiveMarker(markerId)}
              onMouseOut={() => setActiveMarker(null)}
            />
          );
        }

        return (
          <FeatureMarker
            key={stableKey}
            feature={feature}
            featureId={markerId}
            position={{ lat, lng }}
            size={size}
            sizeAsText={String(size)}
            isHovered={isHovered}
            addToList={addToList}
            onMarkerClick={() => handleMarkerClick(markerId)}
            onMouseOver={() => setActiveMarker(markerId)}
            onMouseOut={() => setActiveMarker(null)}
          />
        );
      })}
    </>
  );
}
